import { useEffect, useState } from "react";
import { CheckCircle2, Clock3, Gamepad2, Loader2, MessageCircle, Search, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };
export const STATUS_META = {
  pending: { label: "Menunggu Bayar", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300", icon: Clock3 },
  success: { label: "Sukses", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Ditolak", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300", icon: XCircle },
};
export const bucketOf = (s) => ({ paid: "success", completed: "success", cancelled: "rejected", rejected: "rejected" }[s] || "pending");
export const fmtTime = (iso) => new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export const usePersistedFilter = (name, initial) => {
  const { user } = useAuth();
  const key = `jp_${name}_${user.id}`;
  const [v, setV] = useState(() => localStorage.getItem(key) || initial);
  useEffect(() => {
    localStorage.setItem(key, v);
  }, [key, v]);
  return [v, setV];
};

export const PanelHeader = ({ title, subtitle, pending }) => (
  <div className="space-y-4">
    <div>
      <h2 className="flex flex-wrap items-center gap-3 text-2xl font-extrabold">
        {title}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-300" data-testid="realtime-pill">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Realtime aktif
        </span>
      </h2>
      <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <span className="rounded-xl border border-amber-500/30 bg-[#14141c] px-4 py-2 text-sm font-bold text-amber-300" data-testid="pending-count">Menunggu diproses: {pending}</span>
      <span className="text-xs text-zinc-500">Transaksi yang sudah di-Confirm / Reject otomatis pindah ke menu Riwayat Transaksi.</span>
    </div>
  </div>
);

const UnseenBubble = ({ n, game }) => (n ? (
  <span className="absolute -right-2 -top-2.5 inline-flex animate-bounce items-center gap-0.5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white shadow-[0_0_12px_rgba(244,63,94,0.6)]" data-testid={`game-unseen-${game}`}>
    <MessageCircle size={10} /> {n}
  </span>
) : null);

export const GameFilter = ({ value, onChange, unseen = {} }) => (
  <div className="flex flex-wrap gap-1.5 rounded-xl border border-white/10 bg-[#0f0f15] p-1 pt-2" data-testid="game-filter">
    {[["all", "Semua Game"], ...Object.entries(GAMES)].map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`game-filter-${k}`}
        className={`relative inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors ${value === k ? "bg-[#e5b80b] text-black shadow-[0_0_18px_rgba(229,184,11,0.35)]" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
        <Gamepad2 size={13} /> {l}
        {k !== "all" ? <UnseenBubble n={unseen[k]} game={k} /> : null}
      </button>
    ))}
  </div>
);

export const countUnseen = (rows) => {
  const out = {};
  rows.forEach((r) => { if (!r.seen_at) out[r.product_id] = (out[r.product_id] || 0) + 1; });
  return out;
};

export const SearchBox = ({ value, onChange, placeholder }) => (
  <label className="relative flex min-w-[220px] flex-1 items-center">
    <Search size={15} className="pointer-events-none absolute left-3.5 text-zinc-500" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full rounded-xl border border-white/10 bg-[#0f0f15] py-2.5 pl-10 pr-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#e5b80b]/60" data-testid="tx-search" />
  </label>
);

export const StatusPill = ({ status, testid }) => {
  const m = STATUS_META[bucketOf(status)];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-2.5 py-1 text-[11px] font-bold ${m.cls}`} data-testid={testid}>
      <Icon size={12} /> {m.label}
    </span>
  );
};

export const DecisionButtons = ({ onConfirm, onReject, id }) => {
  const [busy, setBusy] = useState("");
  const run = async (kind, fn) => {
    setBusy(kind);
    await fn();
    setBusy("");
  };
  return (
    <div className="flex gap-1.5">
      <button onClick={() => run("confirm", onConfirm)} disabled={!!busy} data-testid={`confirm-btn-${id}`}
        className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500 hover:text-black disabled:opacity-50">
        {busy === "confirm" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Confirm
      </button>
      <button onClick={() => run("reject", onReject)} disabled={!!busy} data-testid={`reject-btn-${id}`}
        className="inline-flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold text-rose-300 transition-colors hover:bg-rose-500 hover:text-white disabled:opacity-50">
        {busy === "reject" ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
      </button>
    </div>
  );
};

export const filterRows = (rows, game, status, q, fields) => {
  const needle = q.trim().toLowerCase();
  return rows.filter((r) => (game === "all" || r.product_id === game)
    && (status === "all" || bucketOf(r.status) === status)
    && (!needle || fields.some((f) => String(r[f] || "").toLowerCase().includes(needle))));
};

export const TableShell = ({ children, shown, total, testid }) => (
  <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#101017]" data-testid={testid}>
    <div className="border-b border-white/5 px-5 py-3 text-xs text-zinc-500">Menampilkan {shown} dari {total} transaksi</div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">{children}</table>
    </div>
  </div>
);
